import { List, ListItem, Paper } from '@mui/material';
import { IAiModel, IMessageInChat } from '../../api/chat';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeHighlight from 'rehype-highlight';
import 'katex/dist/katex.min.css';
import 'highlight.js/styles/github-dark.css';

import styles from './styles.module.scss';
import { RefObject } from 'react';
import CopyButton from '../../components/CopyButton';

interface MessageListProps {
  items: IMessageInChat[];
  messagesEndRef: RefObject<HTMLDivElement | null>;
  isLoading: boolean;
  aiModelCurrent: IAiModel | undefined;
}

function MessageList({
  items,
  messagesEndRef,
  isLoading,
  aiModelCurrent,
}: MessageListProps) {
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/10 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-xl shadow-xl flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <span className="text-sm text-gray-700">Đang tải tin nhắn...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <List>
        {items.map((message) => (
          <ListItem
            key={message.id}
            sx={{
              justifyContent:
                message.senderRole === 'USER' ? 'flex-end' : 'flex-start',
              position: 'relative',
              px: 0,
            }}
          >
            <Paper
              elevation={1}
              sx={{
                py: { xs: 1, sm: 1.5 },
                px: message.senderRole === 'USER' ? 2 : 0,
                backgroundColor:
                  message.senderRole === 'USER' ? '#F1F1F1B2' : 'white',
                maxWidth: '95%',
                wordBreak: 'break-word',
                color: 'black',
                borderRadius: '8px',
                margin: '0px',
                cursor: 'default',
                position: 'relative',
                boxShadow: 'none',
                marginBottom: '10px',
              }}
            >
              <div>
                {message.senderRole === 'SYSTEM' &&
                message.content.text === '' ? (
                  <span style={{ marginLeft: 4 }}>
                    <span className={styles.loadingDot}></span>
                    <span className={styles.loadingDot}></span>
                    <span className={styles.loadingDot}></span>
                  </span>
                ) : (
                  <div>
                    {message.content.files?.map((file, index) => (
                      <img
                        key={index}
                        src={file.url}
                        alt={file.description}
                        className="h-40 object-cover rounded-lg mb-2"
                      />
                    ))}
                    <div className="relative">
                      {message.senderRole === 'SYSTEM' && (
                        <div className="absolute -top-4 -right-2 z-10">
                          <CopyButton
                            text={message.content.text}
                            tooltipText="Copy message"
                            className={styles.copyButton}
                            size="tiny"
                          />
                        </div>
                      )}
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm, remarkMath]}
                        rehypePlugins={[rehypeKatex, rehypeHighlight]}
                        components={{
                          code: (props) => {
                            const { className, children } = props;
                            const match = /language-(\w+)/.exec(
                              className || ''
                            );
                            const isInline = !match;
                            const codeText = String(children).replace(
                              /\n$/,
                              ''
                            );

                            return isInline ? (
                              <code
                                className={`${styles.inlineCode} ${
                                  className || ''
                                }`}
                                {...props}
                              >
                                {children}
                              </code>
                            ) : (
                              <div className={styles.codeBlock}>
                                <div className={styles.codeHeader}>
                                  <span className={styles.codeLanguage}>
                                    {match ? match[1] : 'text'}
                                  </span>
                                  <CopyButton
                                    text={codeText}
                                    tooltipText="Copy code"
                                    className={styles.codeCopyButton}
                                    size="tiny"
                                  />
                                </div>
                                <code className={className} {...props}>
                                  {children}
                                </code>
                              </div>
                            );
                          },
                        }}
                      >
                        {message.content.text}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            </Paper>
            {message.senderRole !== 'USER' && (
              <div
                className="absolute -top-2 left-0 p-1 bg-white rounded-full"
                style={{
                  boxShadow: '0px 1.96px 1.96px 0px #0000000D',
                }}
              >
                <img
                  src={aiModelCurrent?.aiModelProvider.image}
                  alt="AI Avatar"
                  className="w-4 h-4"
                />
              </div>
            )}
          </ListItem>
        ))}
      </List>
      <div ref={messagesEndRef} />
    </>
  );
}

export default MessageList;
